import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type PropertyRow = Database["public"]["Tables"]["properties"]["Row"];
type PropertyLocation = Pick<Database["public"]["Tables"]["locations"]["Row"], "id" | "name" | "slug" | "area" | "image" | "property_count">;

export type Property = PropertyRow & {
  location_details: PropertyLocation | null;
};

type PropertyQueryFilters = {
  listing_type?: string;
  category?: string;
  is_featured?: boolean;
  location_id?: string;
  exclude_id?: string;
  limit?: number;
  enabled?: boolean;
};

const PROPERTY_SELECT = `
  *,
  location_details:locations(id, name, slug, area, image, property_count)
`;

const normalizeProperty = (property: Property & { location_details: PropertyLocation | PropertyLocation[] | null }) => {
  const locationDetails = Array.isArray(property.location_details)
    ? property.location_details[0] ?? null
    : property.location_details;

  return {
    ...property,
    location: locationDetails?.name ?? property.location,
    location_details: locationDetails,
  } as Property;
};

const sanitizePropertyPayload = (property: Partial<Property>) => ({
  title: property.title ?? "",
  location: property.location ?? "",
  location_id: property.location_id ?? null,
  price: property.price ?? "",
  bedrooms: property.bedrooms ?? 0,
  bathrooms: property.bathrooms ?? 0,
  area: property.area ?? "",
  features: property.features ?? [],
  image: property.image ?? "",
  gallery_images: property.gallery_images ?? [],
  listing_type: property.listing_type ?? "sale",
  category: property.category ?? "ready",
  is_featured: property.is_featured ?? false,
  status_label: property.status_label ?? "For Sale",
  category_label: property.category_label ?? "",
  description: property.description ?? "",
  parking_spaces: property.parking_spaces ?? 0,
  year_built: property.year_built ?? "",
});

export const useProperties = (filters?: PropertyQueryFilters) => {
  return useQuery({
    queryKey: ["properties", filters],
    enabled: filters?.enabled ?? true,
    queryFn: async () => {
      let query = supabase.from("properties").select(PROPERTY_SELECT).order("created_at", { ascending: false });
      if (filters?.listing_type) query = query.eq("listing_type", filters.listing_type);
      if (filters?.category) query = query.eq("category", filters.category);
      if (filters?.is_featured !== undefined) query = query.eq("is_featured", filters.is_featured);
      if (filters?.location_id) query = query.eq("location_id", filters.location_id);
      if (filters?.exclude_id) query = query.neq("id", filters.exclude_id);
      if (filters?.limit) query = query.limit(filters.limit);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((property) => normalizeProperty(property as Property & { location_details: PropertyLocation | PropertyLocation[] | null }));
    },
  });
};

export const useProperty = (id?: string) => {
  return useQuery({
    queryKey: ["properties", "detail", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select(PROPERTY_SELECT)
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;
      return data ? normalizeProperty(data as Property & { location_details: PropertyLocation | PropertyLocation[] | null }) : null;
    },
  });
};

export const usePropertyMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["properties"] });
    qc.invalidateQueries({ queryKey: ["locations"] });
  };

  const create = useMutation({
    mutationFn: async (property: Partial<Property>) => {
      const { data, error } = await supabase
        .from("properties")
        .insert(sanitizePropertyPayload(property))
        .select(PROPERTY_SELECT)
        .single();
      if (error) throw error;
      return normalizeProperty(data as Property & { location_details: PropertyLocation | PropertyLocation[] | null });
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Property> & { id: string }) => {
      const { data, error } = await supabase
        .from("properties")
        .update({ ...sanitizePropertyPayload(updates), updated_at: new Date().toISOString() })
        .eq("id", id)
        .select(PROPERTY_SELECT)
        .single();
      if (error) throw error;
      return normalizeProperty(data as Property & { location_details: PropertyLocation | PropertyLocation[] | null });
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("properties").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
